import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { 
  Globe, 
  Search, 
  Database, 
  Calculator, 
  Shield, 
  CheckCircle,
  AlertTriangle,
  Clock,
  Lock
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";

const steps = [
  {
    icon: <Globe className="h-6 w-6" />,
    title: "You Enter a URL",
    description: "You paste a website address into our checker — like 'example.com' or a full link you received via email or message.",
    detail: "We only process the domain name. No personal data, no browsing history, no tracking.",
  },
  {
    icon: <Search className="h-6 w-6" />,
    title: "We Query Public Data Sources",
    description: "Our system checks the domain against multiple public databases — threat intelligence feeds, domain registration records, and SSL authorities.",
    detail: "These are the same sources security professionals use. We just make them accessible to everyone.",
  },
  {
    icon: <Database className="h-6 w-6" />,
    title: "Signals Are Analyzed",
    description: "Each data source returns a signal — is the domain old or new? Is SSL valid? Has it been reported for phishing or malware?",
    detail: "Individual signals tell part of the story. We look at all of them together.",
  },
  {
    icon: <Calculator className="h-6 w-6" />,
    title: "Signals Are Combined",
    description: "We weigh all signals and combine them into a single risk assessment. No single factor decides the result — it's always a balanced combination.",
    detail: "A new domain isn't automatically flagged as dangerous. A valid SSL doesn't automatically mean safe. Balance matters.",
  },
  {
    icon: <Shield className="h-6 w-6" />,
    title: "You Get a Clear Result",
    description: "You see a clear, color-coded result: Green (Established), Yellow (New/Unknown), Red (Risk), or Teal (Verified by our team).",
    detail: "Verified sites have passed both automatic checks AND a one-time eligibility check against our published criteria.",
  },
];

export default function HowItWorks() {
  return (
    <Layout>
      <Helmet>
        <title>How Our Website Safety Checker Works</title>
        <meta name="description" content="Learn how our free website safety checker evaluates any URL using threat intelligence, domain age, SSL validation, and more. Step-by-step breakdown of our process." />
        <meta property="og:url" content="https://transparencybadge.com/how-it-works" />
        <meta property="og:image" content="https://transparencybadge.com/og-image.png" />
        <link rel="canonical" href="https://transparencybadge.com/how-it-works" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="How It Works"
            title="How We Check If a Website Is Safe"
            description="Here's exactly what happens when you enter a URL into our checker — step by step, with nothing hidden."
          />
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">
          <div className="space-y-0">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {index < steps.length - 1 && (
                  <div className="absolute left-6 top-[4.5rem] bottom-0 w-px bg-border" />
                )}
                
                <div className="relative flex gap-6 pb-12 animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    {step.icon}
                  </div>
                  
                  <div className="flex-1 pt-1">
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Step {index + 1}: {step.title}
                    </h3>
                    <p className="text-muted-foreground mb-3">{step.description}</p>
                    <div className="inline-flex items-start gap-2 rounded-lg bg-accent px-4 py-3">
                      <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm text-foreground">{step.detail}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Principles */}
      <section className="py-16 md:py-20 bg-muted/30 border-y">
        <div className="container">
          <SectionHeader
            badge="Why We're Different"
            title="What Makes This Tool Different from Others"
            description="Most website checkers only check one thing. We check multiple sources and tell you exactly what we found — and what we didn't."
          />

          <div className="grid gap-6 md:grid-cols-3 mt-12 max-w-4xl mx-auto">
            <div className="bg-card rounded-xl border p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-primary mx-auto mb-4">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Multiple Signals, Not One</h3>
              <p className="text-sm text-muted-foreground">
                A website isn't safe just because it has HTTPS. And it's not dangerous just because it's new. We check multiple data points before showing you a result.
              </p>
            </div>

            <div className="bg-card rounded-xl border p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-primary mx-auto mb-4">
                <Clock className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Results Can Change</h3>
              <p className="text-sm text-muted-foreground">
                A website that's safe today could be compromised next week. Our results reflect the data available at the time of your check — not a permanent label.
              </p>
            </div>

            <div className="bg-card rounded-xl border p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-primary mx-auto mb-4">
                <Lock className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Zero Personal Data Collected</h3>
              <p className="text-sm text-muted-foreground">
                We don't know who you are and we don't want to. We process the domain name you enter — nothing else. No tracking, no cookies, no accounts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Deep Explanation Section */}
      <section className="py-16 md:py-20 border-t">
        <div className="container max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            Why Checking a Website Before Trusting It Matters
          </h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Most people assume a website is safe if it looks professional, has a padlock icon, or shows up in a Google search. The reality is more complicated. Scammers have become skilled at copying the visual appearance of legitimate websites — the same fonts, the same layouts, even the same trust badges. What they cannot easily copy are the technical signals that security databases track.
            </p>
            <p>
              When you use a website safety checker, you're not just trusting your eyes. You're pulling from databases that track which domains have been reported for phishing, which domains were registered yesterday versus five years ago, and which SSL certificates belong to real businesses versus throwaway scam operations. That is a fundamentally different kind of check — and it takes about 30 seconds.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">What Is Threat Intelligence and Where Does It Come From?</h3>
            <p>
              Threat intelligence is real-time data collected from security researchers, internet service providers, browser vendors, and automated systems that detect malicious activity. When a phishing site goes live, researchers quickly catalogue it. When malware is distributed through a particular domain, it gets added to abuse lists. These lists are shared across the security community and updated continuously.
            </p>
            <p>
              Our checker queries these same feeds. If a domain you're checking appears in any of them, you'll see a risk warning. If it doesn't, that's one positive signal — but not the only one we check.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">Why Domain Age Is Such a Strong Signal</h3>
            <p>
              The overwhelming majority of scam websites are brand new. There's a simple reason: scammers don't want a permanent digital footprint. They build a site, run it for a few days or weeks until refund requests pile up, then abandon it and start fresh with a new domain. Legitimate businesses build domain reputation over months and years.
            </p>
            <p>
              This doesn't mean every new website is a scam. Plenty of completely legitimate businesses launch every week. What it means is that a domain registered in the last few weeks deserves more scrutiny before you hand over payment details — especially if you found it through a social media ad or forwarded link rather than an organic search.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">What SSL Actually Tells You (and What It Doesn't)</h3>
            <p>
              The padlock icon in your browser's address bar means your connection to that website is encrypted. That's all it means. It does not mean the website is legitimate. It does not mean the business is real. It does not mean your money or data is safe.
            </p>
            <p>
              SSL certificates are free and take minutes to get. Scammers use them routinely. Seeing HTTPS is no longer a trust signal on its own — it's a baseline requirement that both honest and dishonest websites now meet. Our checker notes whether SSL is present, but we never treat it as proof of safety by itself.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">How Results Are Combined Into a Single Score</h3>
            <p>
              Instead of showing you a list of raw data, we combine all the signals into one clear result. A domain that's three years old, has valid SSL, and doesn't appear in any threat databases gets a green "Established" result. A domain registered six days ago that doesn't appear in threat databases yet gets a yellow "New / Unknown" result — not an accusation, just an honest reflection of limited history.
            </p>
            <p>
              A red "Risk" result means we found something concrete: a match in a phishing database, a malware report, or a pattern consistent with known scam infrastructure. At that point, we'd strongly recommend avoiding the site entirely.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">The Difference Between Automatic Checks and the Verified Badge</h3>
            <p>
              Everything described above is automatic — algorithms querying databases, no manual step involved. That's what makes it fast and free. But it has limits. A new, completely legitimate online store will show yellow on our automatic check simply because it's new. Domain age alone can't confirm business legitimacy.
            </p>
            <p>
              That's why we created the Verified Badge Program. Website owners who want a higher trust signal can apply: a one-time eligibility check against our published criteria, followed by ongoing automated monitoring for as long as the subscription is active. It costs $10/month per domain after a free first month, and it's the way to get a result that goes beyond what automated databases alone can confirm. If you run a legitimate website and want to stop showing as "Unknown" to your visitors, that's the program for you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="text-center bg-card border rounded-xl p-8 space-y-4">
              <h2 className="text-xl font-bold text-foreground">See What Data Sources We Use</h2>
              <p className="text-muted-foreground text-sm">
                We list every data source we query. No black boxes, no hidden APIs.
              </p>
              <Button asChild size="lg" className="gradient-hero border-0 w-full">
                <Link to="/data-sources">View Data Sources</Link>
              </Button>
            </div>
            <div className="text-center bg-card border-2 border-primary/30 rounded-xl p-8 space-y-4">
              <h2 className="text-xl font-bold text-foreground">Own a Website?</h2>
              <p className="text-muted-foreground text-sm">
                Get your site verified with our eligibility check and show visitors you're verified — not just "established."
              </p>
              <Button asChild size="lg" className="btn-verified w-full">
                <Link to="/get-verified">Get Verified — First Month Free</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
